import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, fmtMXN, BACKEND } from "@/lib/api";
import { usePresence } from "@/hooks/usePresence";
import { PresenceIndicator } from "@/components/PresenceIndicator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { StatusBar } from "@/components/StatusBar";
import { Chatter } from "@/components/Chatter";
import { Trash2, Plus, Eye, EyeOff, ExternalLink, Lock, Send, ArrowRightLeft, ArrowLeft, Save, FileText, PackagePlus, Check, Paperclip, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const IVA = 0.16;

export default function QuotationEditor() {
  const { id } = useParams();
  const isNew = id === "nueva";
  const quoteViewers = usePresence("quotation", isNew ? null : id, !isNew);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [minMargin, setMinMargin] = useState(15);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([]);
  const [notas, setNotas] = useState("");
  const [terminos, setTerminos] = useState("");
  const [envio, setEnvio] = useState({});
  const [lostOpen, setLostOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [motivoPerdida, setMotivoPerdida] = useState("");
  const [folio, setFolio] = useState("");
  const [estatus, setEstatus] = useState("borrador");
  const [showCosts, setShowCosts] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authKey, setAuthKey] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [poDays, setPoDays] = useState(30);
  const [poFile, setPoFile] = useState(null);
  const [addProd, setAddProd] = useState("");
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [newProd, setNewProd] = useState({ nombre: "", sku: "", unidad: "Pieza", impuesto: "iva16", precio_venta: 0, costo_adquisicion: 0, proveedor_urls: [] });
  const [termTemplates, setTermTemplates] = useState([]);
  const [selTemplate, setSelTemplate] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [clientAccepted, setClientAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderFolio, setOrderFolio] = useState(null);
  const [vigenciaDias, setVigenciaDias] = useState(30);
  const [validUntil, setValidUntil] = useState("");
  const [contactoId, setContactoId] = useState("");

  const readOnly = ["aceptada", "surtido", "enviado", "cerrado"].includes(estatus);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => setMinMargin(r.data.min_margin_pct));
    api.get("/term-templates").then((r) => setTermTemplates(r.data));
    if (isNew) { const c = sp.get("cliente"); if (c) setClientId(c); }
    if (!isNew) {
      api.get(`/quotations/${id}`).then((r) => {
        const q = r.data;
        setClientId(q.client_id); setItems(q.items); setNotas(q.notas || ""); setEstatus(q.estatus);
        setTerminos(q.terminos_condiciones || ""); setFolio(q.folio || "");
        setEnvio(q.direccion_envio || {});
        setMotivoPerdida(q.motivo_perdida || "");
        setAttachments(q.attachments || []); setClientAccepted(!!q.client_accepted); setAcceptedAt(q.accepted_at || null);
        setVigenciaDias(q.vigencia_dias || 30);
        setValidUntil(q.valid_until ? String(q.valid_until).slice(0, 10) : "");
        setContactoId(q.contacto_id || "");
      });
      api.get(`/quotations/${id}/order`).then((r) => { if (r.data && r.data.id) { setOrderId(r.data.id); setOrderFolio(r.data.folio); } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0);
    const iva = items.reduce((s, i) => s + (i.impuesto === "iva16" ? i.cantidad * i.precio_unitario * IVA : 0), 0);
    const costo = items.reduce((s, i) => s + i.cantidad * (i.costo_adquisicion || 0), 0);
    const margen = subtotal - costo;
    const pct = subtotal ? (margen / subtotal) * 100 : 0;
    return { subtotal, iva, total: subtotal + iva, costo, margen, pct };
  }, [items]);

  const belowMin = totals.pct < minMargin && items.length > 0;

  const clientFiscal = (c) => (c?.direccion_facturacion && Object.keys(c.direccion_facturacion).length) ? c.direccion_facturacion : (c?.direccion_envio || {});
  const setEnv = (k, v) => setEnvio((e) => ({ ...e, [k]: v }));
  const copyFiscal = () => { const c = clients.find((x) => x.id === clientId); if (c) setEnvio({ ...clientFiscal(c) }); };

  // Por defecto, la dirección de envío toma el domicilio fiscal del cliente (editable).
  useEffect(() => {
    if (!clientId || !clients.length) return;
    setEnvio((prev) => {
      if (prev && Object.keys(prev).length) return prev;
      const c = clients.find((x) => x.id === clientId);
      return c ? { ...clientFiscal(c) } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients]);

  const itemFromProduct = (p) => ({ product_id: p.id, sku: p.sku, nombre: p.nombre, descripcion: p.descripcion, unidad: p.unidad, cantidad: 1, precio_unitario: p.precio_venta, impuesto: p.impuesto, costo_adquisicion: p.costo_adquisicion, proveedor_urls: p.proveedor_urls || [], extra_urls: [], nota: "" });

  const addProduct = () => {
    const p = products.find((x) => x.id === addProd);
    if (!p) return;
    setItems([...items, itemFromProduct(p)]);
    setAddProd("");
  };

  const createNewProduct = async () => {
    if (!newProd.nombre || !newProd.sku) return toast.error("Nombre y SKU son obligatorios");
    try {
      const { data } = await api.post("/products", newProd);
      setProducts((prev) => [...prev, data]);
      setItems((prev) => [...prev, itemFromProduct(data)]);
      toast.success("Producto agregado al catálogo y a la cotización");
      setNewProdOpen(false);
      setNewProd({ nombre: "", sku: "", unidad: "Pieza", impuesto: "iva16", precio_venta: 0, costo_adquisicion: 0, proveedor_urls: [] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const upd = (idx, k, v) => setItems(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addExtraUrl = (idx) => upd(idx, "extra_urls", [...(items[idx].extra_urls || []), ""]);
  const setExtraUrl = (idx, j, v) => upd(idx, "extra_urls", items[idx].extra_urls.map((u, k) => (k === j ? v : u)));

  const doSave = async (key) => {
    if (!clientId) return toast.error("Selecciona un cliente");
    if (items.length === 0) return toast.error("Agrega al menos un ítem");
    const payload = { client_id: clientId, items, notas, terminos_condiciones: terminos, direccion_envio: envio, authorization_key: key,
      vigencia_dias: Number(vigenciaDias) || 30,
      valid_until: validUntil ? new Date(validUntil + "T12:00:00").toISOString() : null,
      contacto_id: contactoId && contactoId !== "__general__" ? contactoId : null };
    if (isNew && sp.get("qr")) payload.quote_request_id = sp.get("qr");
    try {
      if (isNew) {
        const { data } = await api.post("/quotations", payload);
        toast.success(`Cotización ${data.folio} creada`);
        nav(`/app/cotizaciones/${data.id}`);
      } else {
        await api.put(`/quotations/${id}`, payload);
        toast.success("Cotización guardada");
      }
      setAuthOpen(false); setAuthKey("");
    } catch (e) {
      if (e?.response?.status === 423) { setAuthOpen(true); }
      else toast.error(apiError(e));
    }
  };

  const markLost = async () => {
    if (!motivo.trim()) return toast.error("El motivo es obligatorio");
    try {
      await api.put(`/quotations/${id}/lost`, { motivo: motivo.trim() });
      setEstatus("perdida"); setMotivoPerdida(motivo.trim());
      setLostOpen(false); setMotivo("");
      toast.success("Cotización marcada como no adjudicada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    try {
      const { data } = await api.post(`/quotations/${id}/send`);
      setEstatus("enviada");
      setClientAccepted(false); setAcceptedAt(null);
      if (data.email_status === "accepted") toast.success(`Cotización enviada por correo a ${data.to} (v${data.revision})`);
      else if (data.email_status === "mocked") toast.info("Cotización enviada (correo simulado, Graph no configurado)");
      else toast.warning(`Marcada como enviada, pero el correo no salió (${data.email_status})`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const insertTemplate = () => {
    const t = termTemplates.find((x) => x.id === selTemplate);
    if (!t) return;
    setTerminos((prev) => (prev ? prev + "\n" + t.contenido : t.contenido));
    toast.success("Términos insertados");
  };
  const saveAsTemplate = async () => {
    if (!terminos.trim()) return toast.error("Escribe términos primero");
    const nombre = window.prompt("Nombre de la plantilla:");
    if (!nombre) return;
    try { await api.post("/term-templates", { nombre, contenido: terminos }); const r = await api.get("/term-templates"); setTermTemplates(r.data); toast.success("Plantilla guardada"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadAttachment = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/quotations/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setAttachments((prev) => [...prev, data]); toast.success("Archivo adjuntado"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeAttachment = async (fid) => { await api.delete(`/quotations/${id}/attachments/${fid}`); setAttachments((prev) => prev.filter((a) => a.file_id !== fid)); };

  const doConvert = async () => {
    if (!poNumber) return toast.error("Captura el folio de la P.O. del cliente");
    let fileId = null;
    if (poFile) {
      const fd = new FormData(); fd.append("file", poFile);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      fileId = up.data.file_id;
    }
    try {
      const { data } = await api.post(`/quotations/${id}/convert`, { po_number: poNumber, po_file_id: fileId, condiciones_credito_dias: poDays });
      toast.success(`Pedido ${data.folio} creado`);
      nav(`/app/pedidos/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPdfPreview = () => {
    const c = clients.find((x) => x.id === clientId) || {};
    const fac = c.direccion_facturacion && Object.keys(c.direccion_facturacion || {}).length ? c.direccion_facturacion : (c.direccion_envio || {});
    const today = new Date();
    let venc = new Date();
    if (validUntil) { venc = new Date(validUntil + "T12:00:00"); }
    else { venc.setDate(venc.getDate() + (Number(vigenciaDias) || 30)); }
    const selCt = (clients.find((x) => x.id === clientId)?.contactos || []).find((x) => x.id === contactoId);
    const d = (x) => x.toLocaleDateString("es-MX");
    const esc = (s) => String(s || "").replace(/</g, "&lt;");
    const rows = items.map((it) => `
      <tr>
        <td class="desc"><div class="pname">${esc(it.nombre)}</div>${it.descripcion ? `<div class="pdesc">${esc(it.descripcion)}</div>` : ""}${it.nota ? `<div class="pnote">${esc(it.nota)}</div>` : ""}</td>
        <td class="num">${Number(it.cantidad).toLocaleString("es-MX")} ${esc(it.unidad)}</td>
        <td class="num">${fmtMXN(it.precio_unitario)}</td>
        <td class="num">${fmtMXN(it.cantidad * it.precio_unitario)}</td>
      </tr>`).join("");
    const termsBlock = (terminos || notas)
      ? `<div class="terms"><h4>Términos y condiciones</h4>${esc(terminos)}${notas ? "\n\n" + esc(notas) : ""}</div>` : "";
    const _ak = ["calle", "numero", "colonia", "cp", "ciudad", "estado"];
    const _hasEnv = _ak.some((k) => envio && envio[k]);
    const _diffEnv = _hasEnv && _ak.some((k) => (envio[k] || "") !== (fac[k] || ""));
    const shipBlock = _diffEnv
      ? `<div class="meta" style="margin-top:12px"><div><div class="label">Dirección de envío</div><div class="muted">${esc(envio.calle)} ${esc(envio.numero)}${envio.colonia ? ", Col. " + esc(envio.colonia) : ""}<br/>${envio.cp ? "CP " + esc(envio.cp) : ""} ${esc(envio.ciudad)} ${esc(envio.estado)}${envio.referencias ? "<br/>Ref: " + esc(envio.referencias) : ""}</div></div></div>` : "";
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Cotización ${folio || "Borrador"}</title>
    <style>
      *{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#0A0A0A;margin:0;background:#f3f4f6}
      .toolbar{position:sticky;top:0;background:#0F172A;color:#fff;padding:12px 24px;display:flex;justify-content:space-between;align-items:center}
      .toolbar button{background:#2563EB;color:#fff;border:0;padding:10px 18px;border-radius:6px;font-size:14px;cursor:pointer}
      .page{background:#fff;max-width:800px;margin:24px auto;padding:48px;box-shadow:0 2px 12px rgba(0,0,0,.1)}
      .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0F172A;padding-bottom:16px}
      .logo{width:46px;height:46px;background:#0F172A;color:#fff;display:grid;place-items:center;font-weight:bold;font-size:22px;border-radius:6px}
      .brand{font-size:18px;font-weight:bold;margin-top:8px}.muted{color:#555;font-size:12px;line-height:1.5}
      .tag{color:#E68422;font-style:italic;font-weight:bold;font-size:13px}.title{font-size:26px;font-weight:bold;margin:24px 0 4px}
      .meta{display:flex;justify-content:space-between;margin-top:16px}.meta .label{color:#777;text-transform:uppercase;letter-spacing:.05em;font-size:10px}
      table{width:100%;border-collapse:collapse;margin-top:24px}th{background:#0F172A;color:#fff;text-align:left;padding:8px 10px;font-size:12px}
      th.num,td.num{text-align:right}td{padding:10px;border-bottom:1px solid #e5e7eb;font-size:12px;vertical-align:top}
      .pname{font-weight:600}.pdesc{color:#666;font-size:11px}.pnote{color:#2563EB;font-size:11px;margin-top:3px}
      .totals{margin-top:16px;margin-left:auto;width:260px;font-size:13px}.totals .row{display:flex;justify-content:space-between;padding:4px 0}
      .totals .grand{border-top:2px solid #0F172A;margin-top:6px;padding-top:8px;font-size:16px;font-weight:bold}
      .terms{margin-top:32px;border-top:1px solid #e5e7eb;padding-top:16px;font-size:11px;color:#444;white-space:pre-wrap}
      .terms h4{margin:0 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:.05em}
      .foot{margin-top:40px;border-top:1px solid #e5e7eb;padding-top:12px;text-align:center;font-size:11px;color:#6B7280}
      .pagefoot{position:fixed;bottom:8px;left:0;right:0;text-align:center;font-size:10px;color:#9CA3AF}
      @media print{.toolbar{display:none}body{background:#fff}.page{box-shadow:none;margin:0;max-width:none;padding:24px 24px 60px}}
    </style></head><body>
    <div class="toolbar"><span>Vista previa · Cotización ${folio || "Borrador"}</span><button onclick="window.print()">Descargar / Imprimir PDF</button></div>
    <div class="page">
      <div class="head">
        <div>
          <img src="${window.location.origin}/logo.png" alt="West KAC Company" style="height:46px;display:block"/>
          <div class="muted" style="margin-top:8px">RFC: AEHC950103C1A<br/>Palacio de Justicia #198<br/>25115 Saltillo, COA, México</div>
        </div>
        <div class="tag">Simplifying the way you work.</div>
      </div>
      <div class="title">Cotización ${folio || "(Borrador)"}</div>
      <div class="meta">
        <div><div class="label">Fecha</div><div>${d(today)}</div>
          <div class="label" style="margin-top:8px">Vencimiento</div><div>${d(venc)}</div>
          <div class="label" style="margin-top:8px">Comercial</div><div>${esc((user && user.name) || "")}</div></div>
        <div style="text-align:right"><div class="label">Cliente</div>
          <div style="font-weight:600">${esc(c.razon_social)}</div>
          <div class="muted">${esc(fac.calle)} ${esc(fac.numero)}${fac.colonia ? ", Col. " + esc(fac.colonia) : ""}<br/>${fac.cp ? "CP " + esc(fac.cp) : ""} ${esc(fac.ciudad)} ${esc(fac.estado)}</div>
          <div class="muted">RFC: ${esc(c.rfc)}<br/>${esc(c.regimen_fiscal)}</div>
          ${selCt ? `<div class="muted" style="margin-top:4px">Atn: ${esc(selCt.nombre)}${selCt.email ? " · " + esc(selCt.email) : ""}</div>` : ""}</div>
      </div>
      ${shipBlock}
      <table><thead><tr><th>Descripción</th><th class="num">Cantidad</th><th class="num">Precio unitario</th><th class="num">Importe</th></tr></thead>
        <tbody>${rows}</tbody></table>
      <div class="totals">
        <div class="row"><span>Base imponible</span><span>${fmtMXN(totals.subtotal)}</span></div>
        <div class="row"><span>IVA 16%</span><span>${fmtMXN(totals.iva)}</span></div>
        <div class="row grand"><span>Total</span><span>${fmtMXN(totals.total)}</span></div>
      </div>
      ${termsBlock}
      <div class="foot">ventas@west-kac.com&nbsp;·&nbsp;West KAC Company&nbsp;·&nbsp;west-kac.com</div>
    </div>
    <div class="pagefoot">ventas@west-kac.com · West KAC Company · west-kac.com</div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (w) {
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else toast.error("Permite ventanas emergentes para ver la vista previa");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/app/cotizaciones")}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-2xl font-heading font-bold tracking-tight font-mono">{isNew ? "Nueva cotización" : (folio || "Cotización")}</h1>
        {!isNew && <Badge variant="outline">{{ borrador: "Borrador", enviada: "Enviada", aceptada: "Aceptada", surtido: "En preparación", enviado: "Enviado", cerrado: "Cerrado", perdida: "No adjudicada", vencida: "Vencida" }[estatus] || estatus}</Badge>}
      </div>
      {!isNew && <StatusBar current={estatus} />}
      {!isNew && quoteViewers.length > 0 && <PresenceIndicator viewers={quoteViewers} testid="quote-presence" />}
      {clientAccepted && (
        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm flex items-center gap-2" data-testid="accepted-banner">
          <Check className="w-4 h-4" /> El cliente aceptó esta cotización{acceptedAt ? ` el ${new Date(acceptedAt).toLocaleString("es-MX")}` : ""}.
        </div>
      )}
      {estatus === "perdida" && (
        <div className="p-3 rounded-md bg-red-50 border border-red-300 text-red-800 text-sm flex items-center gap-2" data-testid="lost-banner">
          <Ban className="w-4 h-4" /> Cotización no adjudicada{motivoPerdida ? ` · Motivo: ${motivoPerdida}` : ""}.
        </div>
      )}
      {estatus === "vencida" && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-sm flex items-center gap-2" data-testid="expired-banner">
          <Lock className="w-4 h-4" /> Cotización vencida (vigencia agotada sin respuesta). Puedes reenviarla para reactivarla.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div><Label>Cliente *</Label>
                <Select value={clientId} onValueChange={setClientId} disabled={readOnly}>
                  <SelectTrigger data-testid="quote-client"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!readOnly && (
                <div><Label>Agregar producto</Label>
                  <div className="flex gap-2">
                    <Select value={addProd} onValueChange={setAddProd}>
                      <SelectTrigger data-testid="quote-product-select"><SelectValue placeholder="Buscar por nombre/SKU" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} [{p.sku}]</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={addProduct} data-testid="quote-add-item"><Plus className="w-4 h-4" /></Button>
                    <Button variant="outline" onClick={() => setNewProdOpen(true)} data-testid="quote-new-product" title="Crear producto nuevo"><PackagePlus className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4 pb-4 border-b">
              <div>
                <Label>Contacto de esta cotización</Label>
                <Select value={contactoId} onValueChange={setContactoId} disabled={readOnly}>
                  <SelectTrigger data-testid="quote-contacto"><SelectValue placeholder="Contacto / correo general" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__general__">Correo general del cliente</SelectItem>
                    {(clients.find((c) => c.id === clientId)?.contactos || []).map((ct) => (
                      <SelectItem key={ct.id} value={ct.id}>{ct.nombre || "(sin nombre)"}{ct.email ? ` — ${ct.email}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">A esta persona llegarán la cotización y los correos del pedido. Agrega más contactos en el CRM.</p>
              </div>
              <div>
                <Label>Vigencia de la cotización</Label>
                <div className="flex gap-2 items-center">
                  <Select value={[7, 15, 30].includes(Number(vigenciaDias)) ? String(vigenciaDias) : "custom"} onValueChange={(v) => { if (v !== "custom") { setVigenciaDias(Number(v)); setValidUntil(""); } }} disabled={readOnly}>
                    <SelectTrigger className="w-36" data-testid="quote-vigencia"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 días</SelectItem>
                      <SelectItem value="15">15 días</SelectItem>
                      <SelectItem value="30">30 días</SelectItem>
                      <SelectItem value="custom">Personalizada</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" min="1" className="w-24" value={vigenciaDias} disabled={readOnly} onChange={(e) => { setVigenciaDias(Number(e.target.value)); setValidUntil(""); }} data-testid="quote-vigencia-dias" title="Días de vigencia" />
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-muted-foreground">O fecha de vencimiento exacta (opcional)</Label>
                  <Input type="date" value={validUntil} disabled={readOnly} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="border border-border rounded-lg p-4" data-testid="quote-item">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium">{it.nombre} <span className="text-xs text-muted-foreground font-mono">[{it.sku}]</span></div>
                      <div className="text-xs text-muted-foreground">{it.descripcion}</div>
                    </div>
                    {!readOnly && <Button variant="ghost" size="sm" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div><Label className="text-xs">Cantidad</Label><Input type="number" value={it.cantidad} disabled={readOnly} onChange={(e) => upd(idx, "cantidad", Number(e.target.value))} data-testid="item-qty" /></div>
                    <div><Label className="text-xs">Precio unitario</Label><Input type="number" value={it.precio_unitario} disabled={readOnly} onChange={(e) => upd(idx, "precio_unitario", Number(e.target.value))} data-testid="item-price" /></div>
                    <div><Label className="text-xs">Importe</Label><div className="h-10 flex items-center font-medium">{fmtMXN(it.cantidad * it.precio_unitario)}</div></div>
                  </div>
                  <div className="mt-3"><Label className="text-xs">Nota de la línea (aparece en el PDF)</Label><Textarea value={it.nota || ""} disabled={readOnly} onChange={(e) => upd(idx, "nota", e.target.value)} rows={1} placeholder="Detalle específico de este producto (opcional)" data-testid="item-nota" /></div>
                  {showCosts && (
                    <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="cost-layer">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-amber-700 font-semibold mb-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Capa de costos (privada)</div>
                      <div className="grid grid-cols-2 gap-3 items-end">
                        <div><Label className="text-xs">Costo adquisición</Label><Input type="number" value={it.costo_adquisicion} disabled={readOnly} onChange={(e) => upd(idx, "costo_adquisicion", Number(e.target.value))} data-testid="item-cost" /></div>
                        <div className="text-sm">Margen ítem: <span className="font-semibold">{fmtMXN((it.precio_unitario - it.costo_adquisicion) * it.cantidad)}</span></div>
                      </div>
                      <div className="mt-2 space-y-1">
                        {(it.proveedor_urls || []).map((u, j) => <a key={j} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-interactive underline"><ExternalLink className="w-3 h-3" /> {u}</a>)}
                        {(it.extra_urls || []).map((u, j) => <Input key={j} value={u} placeholder="URL proveedor específica" disabled={readOnly} onChange={(e) => setExtraUrl(idx, j, e.target.value)} className="h-8 text-xs" />)}
                        {!readOnly && <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => addExtraUrl(idx)}><Plus className="w-3 h-3 mr-1" /> Añadir link de compra</Button>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Agrega productos del catálogo.</p>}
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <Label>Términos y condiciones</Label>
                  {!readOnly && (
                    <div className="flex items-center gap-2">
                      <Select value={selTemplate} onValueChange={setSelTemplate}>
                        <SelectTrigger className="h-8 w-48 text-xs" data-testid="term-template-select"><SelectValue placeholder="Insertar plantilla..." /></SelectTrigger>
                        <SelectContent>{termTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" className="h-8" onClick={insertTemplate} data-testid="insert-template">Insertar</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={saveAsTemplate} data-testid="save-as-template">Guardar como plantilla</Button>
                    </div>
                  )}
                </div>
                <Textarea value={terminos} disabled={readOnly} onChange={(e) => setTerminos(e.target.value)} rows={3} placeholder="Ej. Tiempo de envío, condiciones de pago, vigencia, si incluye o no IVA, etc." data-testid="quote-terms" />
              </div>
              <div><Label>Notas generales</Label><Textarea value={notas} disabled={readOnly} onChange={(e) => setNotas(e.target.value)} rows={2} data-testid="quote-notas" /></div>
              <div>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <Label>Dirección de envío</Label>
                  {!readOnly && <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={copyFiscal} data-testid="copy-fiscal-addr">Copiar del domicilio fiscal</Button>}
                </div>
                <p className="text-xs text-muted-foreground mb-2">Por defecto toma el domicilio fiscal del cliente. Edítala si el envío es a otra dirección.</p>
                <div className="grid md:grid-cols-3 gap-2">
                  <Input placeholder="Calle" disabled={readOnly} value={envio?.calle || ""} onChange={(e) => setEnv("calle", e.target.value)} data-testid="envio-calle" />
                  <Input placeholder="Número" disabled={readOnly} value={envio?.numero || ""} onChange={(e) => setEnv("numero", e.target.value)} />
                  <Input placeholder="Colonia" disabled={readOnly} value={envio?.colonia || ""} onChange={(e) => setEnv("colonia", e.target.value)} />
                  <Input placeholder="CP" disabled={readOnly} value={envio?.cp || ""} onChange={(e) => setEnv("cp", e.target.value)} />
                  <Input placeholder="Ciudad" disabled={readOnly} value={envio?.ciudad || ""} onChange={(e) => setEnv("ciudad", e.target.value)} />
                  <Input placeholder="Estado" disabled={readOnly} value={envio?.estado || ""} onChange={(e) => setEnv("estado", e.target.value)} />
                  <Input placeholder="Referencias" className="md:col-span-3" disabled={readOnly} value={envio?.referencias || ""} onChange={(e) => setEnv("referencias", e.target.value)} />
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold">Resumen</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCosts(!showCosts)} data-testid="toggle-costs">{showCosts ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMXN(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA 16%</span><span>{fmtMXN(totals.iva)}</span></div>
              <div className="flex justify-between font-bold text-base pt-2 border-t"><span>Total</span><span data-testid="quote-total">{fmtMXN(totals.total)}</span></div>
            </div>
            {showCosts && (
              <div className={`mt-4 p-3 rounded-md ${belowMin ? "bg-red-50 border border-red-300" : "bg-emerald-50 border border-emerald-200"}`} data-testid="margin-panel">
                <div className="flex justify-between text-sm"><span>Costo total</span><span>{fmtMXN(totals.costo)}</span></div>
                <div className="flex justify-between text-sm"><span>Ganancia (Fee)</span><span className="font-semibold">{fmtMXN(totals.margen)}</span></div>
                <div className="flex justify-between text-sm mt-1"><span>Margen</span><span className={`font-bold ${belowMin ? "text-red-600" : "text-emerald-700"}`} data-testid="margin-pct">{totals.pct.toFixed(2)}%</span></div>
                {belowMin && <div className="text-xs text-red-600 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Bajo el mínimo ({minMargin}%). Requiere clave de administrador.</div>}
              </div>
            )}
          </Card>

          {orderFolio && <Button variant="secondary" className="w-full" onClick={() => nav(`/app/pedidos/${orderId}`)} data-testid="quote-view-order"><ArrowRightLeft className="w-4 h-4 mr-2" /> Ver pedido {orderFolio}</Button>}

          <Button variant="outline" onClick={openPdfPreview} data-testid="quote-pdf-preview"><FileText className="w-4 h-4 mr-2" /> Vista previa / PDF</Button>

          {!readOnly && (
            <div className="flex flex-col gap-2">
              <Button onClick={() => doSave(null)} data-testid="quote-save"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
              {!isNew && (estatus === "borrador" || estatus === "enviada" || estatus === "vencida") && <Button variant="outline" onClick={send} data-testid="quote-send"><Send className="w-4 h-4 mr-2" /> {estatus === "borrador" ? "Enviar al cliente" : "Reenviar cotización"}</Button>}
              {!isNew && <Button variant="secondary" onClick={() => setConvertOpen(true)} data-testid="quote-convert"><ArrowRightLeft className="w-4 h-4 mr-2" /> Convertir a Pedido</Button>}
              {!isNew && ["borrador", "enviada", "vencida"].includes(estatus) && <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setLostOpen(true)} data-testid="quote-lost"><Ban className="w-4 h-4 mr-2" /> Marcar como no adjudicada</Button>}
            </div>
          )}
          {readOnly && <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground flex items-center gap-2"><Lock className="w-4 h-4" /> Cotización aceptada: edición bloqueada para auditoría.</div>}

          {!isNew && (
            <Card className="p-5 rounded-lg">
              <h3 className="font-heading font-semibold mb-1 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Adjuntos</h3>
              <p className="text-xs text-muted-foreground mb-3">Fichas técnicas, presentaciones o imágenes. Se envían al cliente junto con la cotización.</p>
              <div className="space-y-2 mb-3">
                {attachments.map((a) => (
                  <div key={a.file_id} className="flex items-center justify-between text-sm border border-border rounded px-2 py-1" data-testid="attachment-row">
                    <a href={`${BACKEND}/api/files/${a.file_id}`} target="_blank" rel="noreferrer" className="text-interactive underline truncate">{a.filename}</a>
                    {!readOnly && <Button variant="ghost" size="sm" onClick={() => removeAttachment(a.file_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                ))}
                {attachments.length === 0 && <p className="text-xs text-muted-foreground">Sin adjuntos.</p>}
              </div>
              {!readOnly && <Input type="file" onChange={(e) => { uploadAttachment(e.target.files[0]); e.target.value = ""; }} data-testid="attachment-input" />}
            </Card>
          )}

          {!isNew && <Chatter entityType="cotizacion" entityId={id} />}
        </div>
      </div>

      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Autorización de Administrador requerida</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">El margen ({totals.pct.toFixed(2)}%) está por debajo del mínimo ({minMargin}%). Ingresa la clave para autorizar.</p>
          <Input type="password" value={authKey} onChange={(e) => setAuthKey(e.target.value)} placeholder="Clave de autorización" data-testid="auth-key-input" />
          <DialogFooter><Button onClick={() => doSave(authKey)} data-testid="auth-key-submit">Autorizar y guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convertir a Orden de Pedido</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Al convertir, la cotización se congela para auditoría.</p>
          <div><Label>Folio P.O. del cliente *</Label><Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="po-number" /></div>
          <div><Label>Días de crédito</Label><Input type="number" value={poDays} onChange={(e) => setPoDays(Number(e.target.value))} /></div>
          <div><Label>Archivo P.O. (PDF/Imagen)</Label><Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setPoFile(e.target.files[0])} data-testid="po-file" /></div>
          <DialogFooter><Button onClick={doConvert} data-testid="convert-submit">Convertir</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar cotización como no adjudicada</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Se cerrará y saldrá del Pipeline. El motivo es obligatorio para el registro histórico.</p>
          <div><Label>Motivo *</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Ej. Precio por encima de la competencia, tiempo de entrega, sin respuesta del cliente, presupuesto cancelado…" data-testid="lost-motivo" />
          </div>
          <DialogFooter><Button variant="destructive" onClick={markLost} data-testid="lost-submit"><Ban className="w-4 h-4 mr-2" /> Confirmar cierre</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newProdOpen} onOpenChange={setNewProdOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo producto (alta rápida)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Se guarda en el Catálogo y se agrega a la cotización, sin salir de aquí.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nombre *</Label><Input value={newProd.nombre} onChange={(e) => setNewProd({ ...newProd, nombre: e.target.value })} data-testid="np-nombre" /></div>
            <div><Label>SKU *</Label><Input value={newProd.sku} onChange={(e) => setNewProd({ ...newProd, sku: e.target.value })} data-testid="np-sku" /></div>
            <div><Label>Unidad</Label>
              <Select value={newProd.unidad} onValueChange={(v) => setNewProd({ ...newProd, unidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Pieza", "Servicio", "Caja", "Kg", "Litro", "Licencia"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Precio venta</Label><Input type="number" value={newProd.precio_venta} onChange={(e) => setNewProd({ ...newProd, precio_venta: Number(e.target.value) })} data-testid="np-precio" /></div>
            <div><Label>Costo adquisición</Label><Input type="number" value={newProd.costo_adquisicion} onChange={(e) => setNewProd({ ...newProd, costo_adquisicion: Number(e.target.value) })} /></div>
            <div className="col-span-2"><Label>Impuesto</Label>
              <Select value={newProd.impuesto} onValueChange={(v) => setNewProd({ ...newProd, impuesto: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="iva16">IVA 16%</SelectItem><SelectItem value="exento">Exento</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={createNewProduct} data-testid="np-save">Agregar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
